'use client';

import { c as _c } from "react/compiler-runtime";
import { type PostFeedInput } from '@shared/all/validators';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useDebouncedValue from '@shared/utils/hooks/useDebouncedValue';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ImageOff, Loader2 } from 'lucide-react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import PostItem from './PostItem';
type Props = {
  params: PostFeedInput;
  uniqueId?: string;
};
const SEARCH_DEBOUNCE_MS = 400;

// CLIP text->image search results: individual posts ranked by relevance.
const MediaSearchMode = props => {
  const $ = _c(33);
  const {
    localPreferenceState
  } = useLocalPreference();
  const query = useDebouncedValue(localPreferenceState.mediaSearch, SEARCH_DEBOUNCE_MS);
  const factiiiIds = props.params.factiiiIds;
  let t0;
  if ($[0] !== factiiiIds) {
    t0 = factiiiIds ?? [];
    $[0] = factiiiIds;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== query || $[3] !== t0) {
    t1 = ["mediaSearch", query, ...t0];
    $[2] = query;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== factiiiIds || $[6] !== query) {
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return unwrapFromResult(client.posts.mediaSearch.query({
        query,
        factiiiIds,
        cursor: pageParam as number
      }));
    };
    $[5] = factiiiIds;
    $[6] = query;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = query.length > 0;
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      initialPageParam: 0,
      getNextPageParam: _temp,
      enabled: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t4);
  if (isLoading) {
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex items-center justify-center gap-2 p-10 text-sm text-textSecondary"><Loader2 size={16} className="animate-spin" />Searching…</div>;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    return t5;
  }
  if (isError) {
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p className="p-10 text-center text-sm text-destructive">Couldn't search. Try again.</p>;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t5;
  let t6;
  let t7;
  if ($[14] !== data?.pages || $[15] !== props.uniqueId || $[16] !== query) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const posts = data?.pages.flatMap(_temp2) ?? [];
      if (posts.length === 0) {
        let t8;
        if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
          t8 = <ImageOff size={28} strokeWidth={1.5} />;
          $[20] = t8;
        } else {
          t8 = $[20];
        }
        let t9;
        if ($[21] !== query) {
          t9 = <div className="flex flex-col items-center gap-2 p-12 text-center text-textSecondary">{t8}<p className="text-sm">No matches for “{query}”.</p></div>;
          $[21] = query;
          $[22] = t9;
        } else {
          t9 = $[22];
        }
        t7 = t9;
        break bb0;
      }
      t5 = "mt-4 space-y-4";
      let t8;
      if ($[23] !== props.uniqueId) {
        t8 = post => <div key={post.id} className="mb-4" data-post-id={post.id}><PostItem post={post} isFullLength={false} disableLink={false} isInView={true} uniqueId={props.uniqueId} /></div>;
        $[23] = props.uniqueId;
        $[24] = t8;
      } else {
        t8 = $[24];
      }
      t6 = posts.map(t8);
    }
    $[14] = data?.pages;
    $[15] = props.uniqueId;
    $[16] = query;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[25] !== fetchNextPage || $[26] !== hasNextPage || $[27] !== isFetchingNextPage) {
    t8 = hasNextPage && <div className="flex justify-center"><Button variant="SECONDARY" text={isFetchingNextPage ? "Loading\u2026" : "Load more"} loading={isFetchingNextPage} onClick={() => void fetchNextPage()} /></div>;
    $[25] = fetchNextPage;
    $[26] = hasNextPage;
    $[27] = isFetchingNextPage;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t5 || $[30] !== t6 || $[31] !== t8) {
    t9 = <div className={t5}>{t6}{t8}</div>;
    $[29] = t5;
    $[30] = t6;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
};
export default MediaSearchMode;
function _temp(lastPage) {
  return lastPage.nextCursor ?? undefined;
}
function _temp2(page) {
  return page.posts;
}